import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/checkout');
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, navigate]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await fetch(`/api/payments/checkout-status/${sessionId}`);
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        clearCart();
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setAttempts(attempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="text-center">
          <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" style={{ color: '#FF8C19' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#4a2c1a' }}>
            Confirming your payment...
          </h2>
          <p style={{ color: '#736c64' }}>Please wait while we process your order</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <div className="bg-white rounded-lg shadow-lg p-12">
            <CheckCircle className="h-24 w-24 mx-auto mb-6" style={{ color: '#22c55e' }} />
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#4a2c1a' }}>
              Order Complete!
            </h1>
            <p className="text-xl mb-8" style={{ color: '#736c64' }}>
              Thank you for your order! We'll send you a confirmation email shortly with your order details.
            </p>
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/track-order')}
                className="w-full text-white font-semibold py-6 text-lg"
                style={{ backgroundColor: '#FF8C19' }}
              >
                Track Your Order
              </Button>
              <Button
                onClick={() => navigate('/shop')}
                variant="outline"
                className="w-full font-semibold py-6 text-lg border-2"
                style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
              >
                Continue Shopping
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full font-semibold py-6 text-lg border-2"
                style={{ borderColor: '#4a2c1a', color: '#4a2c1a' }}
              >
                Return to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20" style={{ backgroundColor: '#f7f5f2' }}>
      <div className="container mx-auto px-4 max-w-2xl text-center">
        <div className="bg-white rounded-lg shadow-lg p-12">
          <XCircle className="h-24 w-24 mx-auto mb-6" style={{ color: '#dc2626' }} />
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#4a2c1a' }}>
            Payment Error
          </h1>
          <p className="text-xl mb-8" style={{ color: '#736c64' }}>
            There was an issue processing your payment. Please try again or contact us for assistance.
          </p>
          <div className="space-y-4">
            <Button
              onClick={() => navigate('/checkout')}
              className="w-full text-white font-semibold py-6 text-lg"
              style={{ backgroundColor: '#FF8C19' }}
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              className="w-full font-semibold py-6 text-lg border-2"
              style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
              >
                Contact Us
              </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
